import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { useHospiceConvertToPending } from "../../store/useHospiceConvertToPending";
import { TeamsService } from "../../../../../services/teams.service";
import { UserService } from "../../../../../services/user-service";
import { TolerablePainLevels } from "../../../../../enums/enums";
import { ITypeaheadValue } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import {
    IDiagnosis,
    IDiagnosisChangeParameter,
    IDiagnosisValue
} from "../../../../../resources-vue/vue-interfaces/i-diagnosis";
import { IActiveUser, IHosUserResponse } from "../../../../../resources-vue/vue-interfaces/i-user";
import { IGetTeamLean } from "../../../../../resources-vue/vue-interfaces/i-team";
import { IGetUsersSlimRequest } from "../../../../../interfaces/i-user";
import { formatName } from "../../../../../common/format-name";
import { ToastrService } from "../../../../../services/toastr.service";

import Diagnosis from "../../../../../resources-vue/vue-custom-elements/Diagnosis/Diagnosis.vue";
import HospiceDiagnosisTable from "./HospiceDiagnosisTable/HospiceDiagnosisTable.vue";
import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";

export default defineComponent({
    inject: ["v"],
    components: {
        FormSection,
        TypeaheadInput,
        Diagnosis,
        HospiceDiagnosisTable
    },

    data(vm) {
        return {
            _toastrService: null as ToastrService,
            _userService: null,
            _teamsService: null,
            caseManager: null as ITypeaheadValue,
            clinicalManager: null as ITypeaheadValue,
            teams: [] as IGetTeamLean[],
            admittingRN: null as ITypeaheadValue,
            spiritualCounselor: null as ITypeaheadValue,
            socialWorker: null as ITypeaheadValue,
            referralDiagnosis: null as IDiagnosis,
            admittingDiagnosis: null as IDiagnosis,
            hospiceAide: null as ITypeaheadValue,
            volunteerCoordinator: null as ITypeaheadValue,
            bereavementCoordinator: null as ITypeaheadValue,
            TolerablePainLevels: Array.from(TolerablePainLevels.enumWithDescriptions.values()),
            loading: false as boolean,
            v$: this.v
        };
    },

    created() {
        this._teamsService = Container.instance.get(TeamsService);
        this._userService = Container.instance.get(UserService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    mounted() {
        this.initFields();
        this.getTeams();
        if (this.patientDetails) {
            this.referralDiagnosis = this.patientDetails.referringDiagnosis;
            this.admittingDiagnosis = this.patientDetails.admittingDiagnosis;
            if (typeof this.patientDetails.diagnoses == "undefined" || this.patientDetails.diagnoses.length == 0) {
                this.patientDetails.diagnoses = [];
            }
        }
    },

    computed: {
        ...mapState(useHospiceConvertToPending, ["application", "patientDetails", "accountId", "patientId"])
    },

    methods: {
        async initFields() {
            if (this.patientDetails.caseManagerId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.caseManagerId
                };
                const response = await this.getUserById(payload);
                this.caseManager = {
                    name: formatName(response.FirstName, response.LastName, "FN LN"),
                    value: this.patientDetails.caseManagerId
                };
            }
            if (this.patientDetails.clinicalManagerId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.clinicalManagerId
                };
                const response = await this.getUserById(payload);
                this.clinicalManager = {
                    name: formatName(response.FirstName, response.LastName, "FN LN"),
                    value: this.patientDetails.clinicalManagerId
                };
            }

            if (this.patientDetails.admittingNurseId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.admittingNurseId
                };
                let admittingNurse = await this.getUserById(payload);
                if (admittingNurse) {
                    this.admittingRN = {
                        name: formatName(admittingNurse.FirstName, admittingNurse.LastName, "FN LN"),
                        value: this.patientDetails.admittingNurseId
                    };
                }
            }

            if (this.patientDetails.spiritualCounselorId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.spiritualCounselorId
                };
                let spiritualCounselor = await this.getUserById(payload);
                if (spiritualCounselor) {
                    this.spiritualCounselor = {
                        name: formatName(spiritualCounselor.FirstName, spiritualCounselor.LastName, "FN LN"),
                        value: this.patientDetails.spiritualCounselorId
                    };
                }
            }
            if (this.patientDetails.socialWorkerId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.socialWorkerId
                };
                let socialWorker = await this.getUserById(payload);
                if (socialWorker) {
                    this.socialWorker = {
                        name: formatName(socialWorker.FirstName, socialWorker.LastName, "FN LN"),
                        value: this.patientDetails.socialWorkerId
                    };
                }
            }
            if (this.patientDetails.hospiceAideId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.hospiceAideId
                };
                let hospiceAide = await this.getUserById(payload);
                if (hospiceAide) {
                    this.hospiceAide = {
                        name: formatName(hospiceAide.FirstName, hospiceAide.LastName, "FN LN"),
                        value: this.patientDetails.hospiceAideId
                    };
                }
            }
            if (this.patientDetails.volunteerCoordinatorId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.volunteerCoordinatorId
                };
                let volunteerCoordinator = await this.getUserById(payload);
                if (volunteerCoordinator) {
                    this.volunteerCoordinator = {
                        name: formatName(volunteerCoordinator.FirstName, volunteerCoordinator.LastName, "FN LN"),
                        value: this.patientDetails.volunteerCoordinatorId
                    };
                }
            }
            if (this.patientDetails.bereavementCoordinatorId) {
                const payload = {
                    application: Number(this.application),
                    accountId: String(this.accountId),
                    id: this.patientDetails.bereavementCoordinatorId
                };
                let bereavementCoordinator = await this.getUserById(payload);
                if (bereavementCoordinator) {
                    this.bereavementCoordinator = {
                        name: formatName(bereavementCoordinator.FirstName, bereavementCoordinator.LastName, "FN LN"),
                        value: this.patientDetails.bereavementCoordinatorId
                    };
                }
            }
        },
        async getTeams() {
            try {
                if (!!this.patientDetails) {
                    const payload = {
                        providerIds: [this.patientDetails.providerId],
                        locationIds: [this.patientDetails?.locationId],
                        agencyId: this.accountId,
                        accountId: this.accountId,
                        application: this.application
                    };
                    this.teams = await this._teamsService.getTeamsLean(payload);
                }
            } catch (e) {
                console.error(e);
            }
        },

        async getUserById(payload: IGetUsersSlimRequest) {
            const response = await this._userService.getUserDetailsById(payload);
            return response;
        },

        async getUsersList(filter: string, limit: number) {
            const response = await this._userService.getUsersByRole({
                include: true,
                term: filter,
                application: this.application
            });
            return response.map((user: IHosUserResponse) => ({
                name: `${user.firstName} ${user.lastName}`,
                value: user.id
            }));
        },
        async getActiveList(filter: string, limit: number) {
            const response = await this._userService.getActiveList({
                accountId: this.accountId,
                locationIds: [this.patientDetails.locationId],
                term: filter,
                application: Number(this.application)
            });
            return response.map((activeUser: IActiveUser) => ({
                name: `${activeUser.firstName} ${activeUser.lastName}`,
                value: activeUser.id
            }));
        },
        updateCaseManager(newValue: ITypeaheadValue) {
            this.caseManager = newValue;
            this.patientDetails.caseManagerId = newValue.value as string;
        },
        updateClinicalManager(newValue: ITypeaheadValue) {
            this.clinicalManager = newValue;
            this.patientDetails.clinicalManagerId = newValue.value as string;
        },
        updateAdmittingRN(newValue: ITypeaheadValue) {
            this.admittingRN = newValue;
            this.patientDetails.admittingNurseId = newValue.value as string;
        },
        updateSpiritualCounselor(newValue: ITypeaheadValue) {
            this.spiritualCounselor = newValue;
            this.patientDetails.spiritualCounselorId = newValue.value as string;
        },
        updateSocialWorker(newValue: ITypeaheadValue) {
            this.socialWorker = newValue;
            this.patientDetails.socialWorkerId = newValue.value as string;
        },
        referralDiagnosisChanged(diag: IDiagnosisChangeParameter) {
            this.referralDiagnosis = diag.newDiagnosis;
            this.patientDetails.referringDiagnosis = diag?.newDiagnosis;
        },
        admittingDiagnosisChanged(diag: IDiagnosisChangeParameter) {
            this.admittingDiagnosis = diag.newDiagnosis;
            this.patientDetails.admittingDiagnosis = diag?.newDiagnosis;
        },
        updateDiagnosis(value: Array<IDiagnosisValue>) {
            this.patientDetails.diagnoses = value;
        },
        updateHospiceAide(newValue: ITypeaheadValue) {
            this.hospiceAide = newValue;
            this.patientDetails.hospiceAideId = newValue.value as string;
        },
        updatevolunteerCoordinator(newValue: ITypeaheadValue) {
            this.volunteerCoordinator = newValue;
            this.patientDetails.volunteerCoordinatorId = newValue.value as string;
        },
        updatebereavementCoordinator(newValue: ITypeaheadValue) {
            this.bereavementCoordinator = newValue;
            this.patientDetails.bereavementCoordinatorId = newValue.value as string;
        },
        async handleCareTeamChange() {
            try {
                this.loading = true;
                const payload = {
                    teamId: this.patientDetails.careTeamId,
                    accountId: this.accountId,
                    application: this.application
                };
                const response = await this._teamsService.getTeamById(payload);
                await this.getUsersList(response.clinicalManagerFirstName, 2);
                const data = {
                    name: formatName(response.clinicalManagerFirstName, response.clinicalManagerLastName, "FN LN"),
                    value: response.clinicalManagerId
                };
                this.updateClinicalManager(data);
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: "Failed to fetch Clinical Manager",
                    message: "Try Again!!"
                });
            } finally {
                this.loading = false;
            }
        }
    }
});
